import type { Scope, Translations } from "@/src/i18n/translations";
import { en, fr } from "@/src/i18n/translations";

/** Les langues servies par l'admin. L'ordre est celui du sélecteur. */
export const LOCALES = ["en", "fr"] as const;

export type Locale = (typeof LOCALES)[number];

/**
 * Langue par défaut.
 *
 * L'anglais, et non le français des dépôts voisins : l'admin peut être ouverte
 * par quelqu'un qui ne lit pas le français, alors qu'un francophone retrouve sa
 * langue en deux clics — et le choix est mémorisé.
 */
export const DEFAULT_LOCALE: Locale = "en";

const DICTIONARIES: Record<Locale, Translations> = { en, fr };

/** Valeurs interpolables dans une traduction. */
export type TranslationParams = Record<string, string | number>;

/** Reconnaît une langue servie par l'admin — tout le reste retombe sur `en`. */
export function isLocale(value: string | null | undefined): value is Locale {
  return LOCALES.some((locale) => locale === value);
}

/**
 * Remplace les `{{jetons}}` d'une traduction par leurs valeurs.
 *
 * Cinq lignes qui évitent une famille entière de bugs : sans elles, la seule
 * façon d'insérer une valeur dans une phrase est la concaténation, qui **fige
 * l'ordre des mots**. « 3 comptes sélectionnés » et « 3 selected accounts » ne
 * se découpent pas au même endroit ; en concaténant, la traduction devient
 * fausse dans une langue sur deux sans que rien ne le signale.
 *
 * Un jeton sans valeur est laissé tel quel plutôt que remplacé par « undefined »
 * : le trou se voit à l'écran, ce qui est exactement ce qu'on veut d'une
 * traduction incomplète.
 */
export function interpolate(
  template: string,
  params: TranslationParams,
): string {
  return template.replace(/\{\{(\w+)\}\}/g, (token, name: string) => {
    const value = params[name];
    return value === undefined ? token : String(value);
  });
}

/**
 * Traduit une clé — cœur pur du moteur, sans React.
 *
 * Les composants ne l'appellent pas directement : ils passent par
 * `useTranslations(scope)`, qui fixe la langue et le scope.
 */
export function translate<S extends Scope, K extends keyof Translations[S]>(
  locale: Locale,
  scope: S,
  key: K,
  params?: TranslationParams,
): string {
  // ⚠️ Le `as string` n'est pas de la paresse : `Translations[S][K]` est un
  // accès indexé *générique*, et TypeScript ne sait pas prouver qu'il vaut
  // `string` tant que `S` et `K` ne sont pas résolus — alors même que toutes
  // les feuilles du dictionnaire en sont. Le retirer sort deux TS2345/TS2322.
  const value = DICTIONARIES[locale][scope][key] as string;

  return params ? interpolate(value, params) : value;
}
